import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { buildApiUrl } from "../utils/api";

function WishList() {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [actionMsg, setActionMsg] = useState({ text: "", type: "" });
    const navigate = useNavigate();

    const showMsg = (text, type = "success") => {
        setActionMsg({ text, type });
        setTimeout(() => setActionMsg({ text: "", type: "" }), 3500);
    };

    useEffect(() => {
        const fetchWishlist = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/userLogin');
                return;
            }

            try {
                const response = await axios.get(buildApiUrl('/api/userWishList/getUserWishList'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setWishlistItems(Array.isArray(response.data) ? response.data : []);
            } catch (err) {
                setPageError("Could not load your saved list. Please try again.");
                console.error("Error fetching wishlist:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchWishlist();
    }, [navigate]);

    const handleRemoveClick = async (itemId) => {
        try {
            await axios.delete(buildApiUrl(`/api/userWishList/delete/${itemId}`));
            // Filter by the wishlist record _id, not by userId/warehouseId combo
            setWishlistItems(prev => prev.filter(item => item._id !== itemId));
            showMsg("Removed from saved list.");
        } catch (error) {
            console.error("Error deleting wishlist item:", error);
            showMsg("Failed to remove the item. Please try again.", "error");
        }
    };

    const handleQuoteNow = (warehouseId) => {
        navigate(`/warehouseDetails/${warehouseId}`);
    };

    if (loading) {
        return <div className="p-4 text-center text-muted">Loading your saved warehouses...</div>;
    }

    if (pageError) {
        return <div className="p-4 text-center text-danger">{pageError}</div>;
    }

    return (
        <>
            <Helmet>
                <title>Saved Warehouses | 3PL Search</title>
            </Helmet>

            <div className="wishlist p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 style={{ color: "#132238" }}>Saved Warehouses</h2>
                    {wishlistItems.length > 0 && (
                        <span className="badge bg-secondary">{wishlistItems.length} saved</span>
                    )}
                </div>

                {actionMsg.text && (
                    <div className={`alert alert-${actionMsg.type === "error" ? "danger" : "success"} py-2`}>
                        {actionMsg.text}
                    </div>
                )}

                {wishlistItems.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <p className="mb-3">You have no saved warehouses yet.</p>
                        <button className="btn btn-primary" onClick={() => navigate('/finder')}>
                            Browse warehouses
                        </button>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                            <thead className="thead-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Warehouse Name</th>
                                    <th>Location</th>
                                    <th>Total Area</th>
                                    <th>Date Saved</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wishlistItems.map((item, index) => (
                                    <tr key={item._id}>
                                        <td>{index + 1}</td>
                                        <td>{item.warehouseName}</td>
                                        <td>{item.location}</td>
                                        <td>{item.area}{item.area && !String(item.area).includes('sq') ? ' sq.ft' : ''}</td>
                                        <td>{new Date(item.dateAdded).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => handleQuoteNow(item.warehouseId)}
                                                >
                                                    Request Quote
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleRemoveClick(item._id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

export default WishList;
